import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';

import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Input } from '../../components/common/Input';
import { Select } from '../../components/common/Select';
import { Textarea } from '../../components/common/Textarea';
import { Checkbox } from '../../components/common/Checkbox';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { pimService } from '../../services/pimService';
import type { 
  Category,
  CategoryFormData 
} from '../../types/pim';

export const CategoryForm = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { showNotification } = useNotification();
  const queryClient = useQueryClient();

  const isEdit = !!id && id !== 'new';
  const categoryId = isEdit ? parseInt(id) : null;

  const [formData, setFormData] = useState<CategoryFormData>({
    name: '',
    slug: '',
    description: '',
    parentId: undefined,
    sortOrder: 1,
    isActive: true,
    imageUrl: '',
    metaTitle: '',
    metaDescription: ''
  });

  const [errors, setErrors] = useState<Partial<CategoryFormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch category data for editing
  const { data: categoryData, isLoading: loadingCategory } = useQuery({
    queryKey: ['pim-category', categoryId],
    queryFn: () => pimService.getCategory(categoryId!),
    enabled: isEdit,
    onError: (error: any) => {
      showNotification('Błąd podczas ładowania kategorii', 'error');
      navigate('/pim/categories');
    }
  });

  // Set form data when category data is loaded
  useEffect(() => {
    if (categoryData?.data && isEdit) {
      const category = categoryData.data;
      setFormData({
        name: category.name || '',
        slug: category.slug || '',
        description: category.description || '',
        parentId: category.parent_id || undefined,
        sortOrder: category.sort_order || 1,
        isActive: Boolean(category.is_active),
        imageUrl: category.image_url || '',
        metaTitle: category.meta_title || '',
        metaDescription: category.meta_description || ''
      });
    }
  }, [categoryData, isEdit]);

  // Fetch parent categories for dropdown
  const { data: parentCategoriesData } = useQuery({
    queryKey: ['pim-categories-for-parent'],
    queryFn: () => pimService.getCategories({
      includeInactive: false,
      limit: 1000 // Get all active categories
    }),
    onError: (error: any) => {
    }
  });

  // Create/Update mutations
  const createMutation = useMutation({
    mutationFn: (data: CategoryFormData) => pimService.createCategory(data),
    onSuccess: () => {
      showNotification('Kategoria została pomyślnie utworzona', 'success');
      queryClient.invalidateQueries(['pim-categories']);
      navigate('/pim/categories');
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas tworzenia kategorii', 'error');
      setIsSubmitting(false);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: CategoryFormData) => pimService.updateCategory(categoryId!, data),
    onSuccess: () => {
      showNotification('Kategoria została pomyślnie zaktualizowana', 'success');
      queryClient.invalidateQueries(['pim-categories']);
      queryClient.invalidateQueries(['pim-category', categoryId]);
      navigate('/pim/categories');
    },
    onError: (error: any) => {
      showNotification(error.message || 'Błąd podczas aktualizacji kategorii', 'error');
      setIsSubmitting(false);
    }
  });

  const handleInputChange = (field: keyof CategoryFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from name
    if (field === 'name' && !isEdit) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9ąćęłńóśźż\s-]/g, '')
        .replace(/[ąćęłńóśźż]/g, (char: string) => {
          const map: Record<string, string> = {
            'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n',
            'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z'
          };
          return map[char] || char;
        })
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '');
      
      setFormData(prev => ({
        ...prev,
        slug
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CategoryFormData> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nazwa jest wymagana';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug jest wymagany';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug może zawierać tylko małe litery, cyfry i myślniki';
    }

    if (formData.sortOrder < 0) {
      newErrors.sortOrder = 'Pozycja nie może być ujemna';
    }

    if (formData.imageUrl.trim() && !isValidUrl(formData.imageUrl.trim())) {
      newErrors.imageUrl = 'Nieprawidłowy adres URL obrazu';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Convert form data to API format
    const submitData: CategoryFormData = {
      ...formData,
      parentId: formData.parentId || undefined,
      sortOrder: Number(formData.sortOrder),
      imageUrl: formData.imageUrl.trim() || undefined,
      metaTitle: formData.metaTitle.trim() || undefined,
      metaDescription: formData.metaDescription.trim() || undefined
    };

    if (isEdit) {
      updateMutation.mutate(submitData);
    } else {
      createMutation.mutate(submitData);
    }
  };

  // Filter out current category and its descendants from parent options
  const getParentOptions = () => {
    if (!parentCategoriesData?.data) return [];

    let options = parentCategoriesData.data;
    
    // If editing, exclude current category and its descendants
    if (isEdit && categoryData?.data) {
      const currentCategory = categoryData.data;
      options = options.filter(cat => {
        // Don't allow self as parent
        if (cat.id === currentCategory.id) return false;
        
        // Don't allow descendants as parents (would create circular reference)
        if (cat.path?.includes(`/${currentCategory.slug}/`)) return false;
        
        return true;
      });
    }

    return [
      { value: '', label: 'Brak (kategoria główna)' },
      ...options.map(cat => ({
        value: cat.id.toString(),
        label: `${'  '.repeat(cat.level)}${cat.name}` // Indent based on level
      }))
    ];
  };

  if (isEdit && loadingCategory) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <p className="mt-2 text-gray-600">Ładowanie kategorii...</p>
      </div>
    );
  }

  if (!permissions.canWriteProducts) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Brak uprawnień do zarządzania kategoriami</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            icon={ArrowLeftIcon}
            onClick={() => navigate('/pim/categories')}
          >
            Powrót
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Edytuj kategorię' : 'Nowa kategoria'}
            </h1>
            <p className="text-gray-600">
              {isEdit ? 'Modyfikuj istniejącą kategorię' : 'Utwórz nową kategorię produktów'}
            </p>
          </div>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            icon={XMarkIcon}
            onClick={() => navigate('/pim/categories')}
          >
            Anuluj
          </Button>
          <Button
            variant="primary"
            icon={CheckIcon}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Zapisywanie...' : (isEdit ? 'Aktualizuj' : 'Utwórz')}
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6" key={categoryData?.data?.id || 'new'}>
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Podstawowe informacje</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('pim.categories.form.name')} required
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={errors.name}
                placeholder="np. Elektronika, Odzież"
                required
              />
              <Input
                label={t('pim.categories.form.slug')} required
                value={formData.slug}
                onChange={(e) => handleInputChange('slug', e.target.value)}
                error={errors.slug}
                placeholder="np. elektronika, odziez"
                help="Używany w adresach URL. Tylko małe litery, cyfry i myślniki."
                required
              />
            </div>
            
            <Textarea
              label={t('pim.categories.form.description')}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              error={errors.description}
              placeholder="Opcjonalny opis kategorii"
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Hierarchy & Organization */}
        <Card>
          <CardHeader>
            <CardTitle>Hierarchia i organizacja</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label={t('pim.categories.form.parentCategory')}
                value={formData.parentId?.toString() || ''}
                onChange={(e) => handleInputChange('parentId', e.target.value ? parseInt(e.target.value) : undefined)}
                options={getParentOptions()}
                help="Pozostaw puste dla kategorii głównej"
              />
              <Input
                label={t('pim.categories.form.sortOrder')}
                type="number"
                value={formData.sortOrder}
                onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 1)}
                error={errors.sortOrder}
                placeholder="0"
                help="Niższe wartości wyświetlane są pierwsze"
                min="0"
              />
            </div>

            <div className="flex items-center space-x-3">
              <Checkbox
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
              <label className="text-sm font-medium text-gray-700">
                Kategoria aktywna
              </label>
              <span className="text-sm text-gray-500">
                Tylko aktywne kategorie są widoczne w sklepie
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Media & SEO */}
        <Card>
          <CardHeader>
            <CardTitle>Multimedia i SEO</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              label={t('pim.categories.form.imageUrl')}
              value={formData.imageUrl}
              onChange={(e) => handleInputChange('imageUrl', e.target.value)}
              error={errors.imageUrl}
              placeholder="https://example.com/image.jpg"
              help="Opcjonalny obraz reprezentujący kategorię"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('pim.categories.form.seoTitle')}
                value={formData.metaTitle}
                onChange={(e) => handleInputChange('metaTitle', e.target.value)}
                error={errors.metaTitle}
                placeholder="Tytuł strony dla wyszukiwarek"
                maxLength={255}
                help={`${formData.metaTitle.length}/255 znaków`}
              />
              <Textarea
                label={t('pim.categories.form.seoDescription')}
                value={formData.metaDescription}
                onChange={(e) => handleInputChange('metaDescription', e.target.value)}
                error={errors.metaDescription}
                placeholder="Opis strony dla wyszukiwarek"
                rows={3}
                maxLength={1000}
                help={`${formData.metaDescription.length}/1000 znaków`}
              />
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        {(formData.name || formData.description) && (
          <Card>
            <CardHeader>
              <CardTitle>Podgląd</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex items-center space-x-3">
                  <FolderIcon className="h-8 w-8 text-primary-600" />
                  <div>
                    <h3 className="font-medium text-gray-900">{formData.name || 'Nazwa kategorii'}</h3>
                    {formData.description && (
                      <p className="text-sm text-gray-600 mt-1">{formData.description}</p>
                    )}
                    <div className="text-xs text-gray-500 mt-2">
                      URL: /categories/{formData.slug || 'slug'}
                      {formData.parentId && parentCategoriesData?.data && (
                        <>
                          {' • '}
                          Rodzic: {parentCategoriesData.data.find(c => c.id === formData.parentId)?.name}
                        </>
                      )}
                      {' • '}
                      Pozycja: {formData.sortOrder}
                      {' • '}
                      Status: {formData.isActive ? 'Aktywna' : 'Nieaktywna'}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </form>
    </div>
  );
};